import React, { Fragment } from 'react';
import { withSnackbar, ProviderContext } from 'notistack';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Tooltip,
  Typography
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { CloudUploadOutlined } from '@material-ui/icons';
import GetAppOutlinedIcon from '@material-ui/icons/GetAppOutlined';
import DescriptionOutlinedIcon from '@material-ui/icons/DescriptionOutlined';
import FileUploadRow from '../../_shared/FileUploadRow';
import { ScaleLoader } from 'react-spinners';
import MaterialTable from 'material-table';
import { ValidatorForm } from 'react-material-ui-form-validator';
import { ApplicationContext, AppContext } from '../../../context/Contexts';
import { ICaseService } from '../../../services/Interfaces/ICaseService';
import { ImportTemplateCodeEnum } from '../../../helpers/Constants';
import { ITranslatorService } from '../../../services/Interfaces/ITranslatorService';
import { IOrganizationService } from '../../../services/Interfaces/IOrganizationService';
import { getLocalization, MaterialTableIcons } from '../../_shared/TableSettings';
import {
  ImportOrganizationAttachment,
  ImportOrganizationsRequestDto,
  OrganizationImportItemDto
} from '../../../interfaces/Organization';

interface IOrganizationImportState {
  isImportDialogOpen: boolean;
  isLoadingImportData: boolean;
  isDownloading: boolean;
  attachments: ImportOrganizationAttachment[];
  importedOrganizationList: OrganizationImportItemDto[];
}

interface IOrganizationImportOwnProps {
  onClose?: () => void;
}

type IOrganizationImportProps = ProviderContext & IOrganizationImportOwnProps;

class OrganizationImport extends React.PureComponent<IOrganizationImportProps, IOrganizationImportState> {
  private translatorService!: ITranslatorService;
  private organizationService!: IOrganizationService;
  private caseService!: ICaseService;

  static contextType = ApplicationContext;

  state = {
    isImportDialogOpen: false,
    isLoadingImportData: false,
    isDownloading: false,
    attachments: [] as ImportOrganizationAttachment[],
    importedOrganizationList: [] as OrganizationImportItemDto[]
  } as IOrganizationImportState;

  openImportDialog = () => {
    this.setState({
      isImportDialogOpen: true,
      isLoadingImportData: false,
      attachments: [],
      importedOrganizationList: []
    });
  };

  closeImportDialog = () => {
    this.setState({
      isImportDialogOpen: false,
      attachments: [],
      importedOrganizationList: []
    });

    if (this.props.onClose) {
      this.props.onClose();
    }
  };

  handleDownloadTemplate = async () => {
    if (this.state.isDownloading) return;
    this.setState({ isDownloading: true });
    try {
      const blob = await this.caseService.GetImportModel(
        ImportTemplateCodeEnum.ORGANIZATION_IMPORT_TEMPLATE
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'Import organizations.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('ERROR_MSG', 'Eroare'),
        { variant: 'error' }
      );
    } finally {
      this.setState({ isDownloading: false });
    }
  };

  handleDeleteAttachment = (id: number) => {
    const attachments = this.state.attachments.filter((a) => a.id !== id);
    this.setState({ attachments });
  };

  handleImport = async () => {
    if (this.state.attachments.length === 0) {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate(
          'ERROR_MSG_FILE_NOT_SELECTED',
          'Va rugam sa selectati un fisier!'
        ),
        { variant: 'error' }
      );
      return;
    }
    this.setState({ isLoadingImportData: true });

    const request = {
      attachments: this.state.attachments
    } as ImportOrganizationsRequestDto;

    try {
      const result = await this.organizationService.SaveImportedOrganizations(request);
      this.props.enqueueSnackbar(
        <div>
          <p>
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_DATA_PROCESSED',
              'Date tratate: '
            )}{' '}
            {result.totalNumber}
          </p>
          <p>
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_TREATED_SUCCESSFULLY',
              'Tratate cu succes: '
            )}{' '}
            {result.importedNumber}
          </p>
        </div>,
        { variant: 'info', autoHideDuration: 6000 }
      );
      this.setState({
        importedOrganizationList: result.organizationList,
        isLoadingImportData: false
      });
    } catch {
      this.props.enqueueSnackbar(
        this.translatorService.Tranlate('ERROR_MSG', 'Eroare'),
        { variant: 'error', autoHideDuration: 6000 }
      );
      this.setState({ isLoadingImportData: false });
    }
  };

  renderInfoRow = (
    iconNode: React.ReactNode,
    iconBgColor: string,
    title: string,
    description: React.ReactNode,
    actionNode: React.ReactNode
  ) => (
    <Paper
      variant="outlined"
      className="p-3 mb-3 d-flex flex-row align-items-center"
      style={{ borderRadius: 8 }}
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="center"
        style={{
          width: 44,
          height: 44,
          borderRadius: 6,
          backgroundColor: iconBgColor,
          marginRight: 16
        }}
      >
        {iconNode}
      </Box>
      <Box flexGrow={1}>
        <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography variant="body2" color="textSecondary">
          {description}
        </Typography>
      </Box>
      {actionNode}
    </Paper>
  );

  handleUploadFileSelected = (file: File) => {
    const attachments = [...this.state.attachments];
    attachments.push({
      id: 0,
      file,
      fileName: file.name,
      uploadDate: new Date(),
      originalFileName: file.name
    });
    this.setState({ attachments });
  };

  handleUploadFileRemoved = () => {
    const attachment = this.state.attachments[0];
    if (attachment) {
      this.handleDeleteAttachment(attachment.id);
    }
  };

  renderAttachmentBox = () => {
    const attachment = this.state.attachments[0];

    return (
      <Box className="mt-2 mb-4">
        {this.renderInfoRow(
          <DescriptionOutlinedIcon style={{ color: '#FF7345' }} />,
          '#FFF5F2',
          this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ALLOWED_FORMAT',
            'Format fisier acceptat: .XLSX'
          ),
          this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ALLOWED_FORMAT_DESC',
            'Descarca modelul si pastreaza structura asteptata inainte de a importa organizatiile.'
          ),
          <Button
            variant="contained"
            color="secondary"
            onClick={this.handleDownloadTemplate}
            disabled={this.state.isDownloading}
            startIcon={<GetAppOutlinedIcon />}
            style={{
              textTransform: 'none',
              fontWeight: 600,
              whiteSpace: 'nowrap'
            }}
          >
            {this.translatorService.Tranlate(
              'ORGANIZATIONS_IMPORT_DOWNLOAD_TEMPLATE',
              'Descarca model'
            )}
          </Button>
        )}

        <FileUploadRow
          inputId="organization-import-file"
          title={this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ADD_FILE',
            'Adauga fisier organizatii'
          )}
          emptyDescription={this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_ADD_FILE_DESC',
            'Selecteaza un fisier .xlsx'
          )}
          uploadButtonLabel={this.translatorService.Tranlate(
            'ORGANIZATIONS_IMPORT_UPLOAD_FILE',
            'Incarca fisier'
          )}
          accept=".xls,.xlsx"
          fileName={attachment?.originalFileName ?? null}
          onFileSelected={this.handleUploadFileSelected}
          onFileRemoved={this.handleUploadFileRemoved}
        />
      </Box>
    );
  };

  getImportedColumns = () => [
    {
      field: 'organizationType',
      title: this.translatorService.Tranlate(
        'EDIT_ORGANIZATION_ORGANIZATION_TYPE_LABEL',
        'Tip Organizatie'
      )
    },
    {
      field: 'companyNumber',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_COMPANY_NUMBER_LABEL', 'CUI')
    },
    {
      field: 'parentCompanyNumber',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_PARENT_CUI',
        'CUI Entitate parinte'
      )
    },
    {
      field: 'parentName',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_PARENT_NAME',
        'Nume Entitate parinte'
      )
    },
    {
      field: 'name',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_NAME_LABEL', 'Nume')
    },
    {
      field: 'registryNumber',
      title: this.translatorService.Tranlate(
        'EDIT_ORGANIZATION_REGISTRY_NUMBER_LABEL',
        'Nr. Reg. Comertului'
      )
    },
    {
      field: 'countryName',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_COUNTRY_LABEL', 'Tara')
    },
    {
      field: 'regionName',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_COUNTY_LABEL', 'Judet')
    },
    {
      field: 'cityName',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_CITY_1_LABEL', 'Oras')
    },
    {
      field: 'address',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_ADDRESS_LABEL', 'Adresa')
    },
    {
      field: 'postalCode',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_POSTALCODE_LABEL', 'Cod Postal')
    },
    {
      field: 'phone',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_PHONE_LABEL', 'Telefon')
    },
    {
      field: 'email',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_EMAIL_LABEL', 'E-mail')
    },
    {
      field: 'newFeature',
      title: this.translatorService.Tranlate('COPY_CASE', 'Copiaza dosar'),
      render: (row: OrganizationImportItemDto) => (row.newFeature ? '1' : '0')
    },
    {
      field: 'uploadFeature',
      title: this.translatorService.Tranlate('UPLOAD_MESSAGE', 'Caracteristici Upload'),
      render: (row: OrganizationImportItemDto) => (row.uploadFeature ? '1' : '0')
    },
    {
      field: 'showOrderDetails',
      title: this.translatorService.Tranlate('ORDERS_DIALOG_TITLE', 'Detalii comanda'),
      render: (row: OrganizationImportItemDto) => (row.showOrderDetails ? '1' : '0')
    },
    {
      field: 'changeCaseStatusBasedCalculation',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_CHANGE_CASE_STATUS_BASED_CALCULATION',
        'Stare calculatie'
      ),
      render: (row: OrganizationImportItemDto) =>
        row.changeCaseStatusBasedCalculation ? '1' : '0'
    },
    {
      field: 'bulkNotification',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_BULK_NOTIFICATION',
        'Notificari cu istoric'
      ),
      render: (row: OrganizationImportItemDto) => (row.bulkNotification ? '1' : '0')
    },
    {
      field: 'isMavoPartener',
      title: this.translatorService.Tranlate('MAVO_ORG_PARTNER', 'Partener Mavo'),
      render: (row: OrganizationImportItemDto) => (row.isMavoPartener ? '1' : '0')
    },
    {
      field: 'isCarfixPartener',
      title: this.translatorService.Tranlate('CARFIX_ORG_PARTNER', 'Partener Carfix App'),
      render: (row: OrganizationImportItemDto) => (row.isCarfixPartener ? '1' : '0')
    },
    {
      field: 'isFleetPartner',
      title: this.translatorService.Tranlate('FLEET_ORG_PARTNER', 'Partener Fleet'),
      render: (row: OrganizationImportItemDto) => (row.isFleetPartner ? '1' : '0')
    },
    {
      field: 'isVodafonePartner',
      title: this.translatorService.Tranlate('VODAFONE_ORG_PARTNER', 'Partener Vodafone'),
      render: (row: OrganizationImportItemDto) => (row.isVodafonePartner ? '1' : '0')
    },
    {
      field: 'hasAppointmentWorkflow',
      title: this.translatorService.Tranlate(
        'CARFIX_ORG_APPOINTMENT_WORKFLOW',
        'Programare simpla'
      ),
      render: (row: OrganizationImportItemDto) => (row.hasAppointmentWorkflow ? '1' : '0')
    },
    {
      field: 'hasRepairWorkflow',
      title: this.translatorService.Tranlate('CARFIX_ORG_REPAIR_WORKFLOW', 'Flux Reparatie'),
      render: (row: OrganizationImportItemDto) => (row.hasRepairWorkflow ? '1' : '0')
    },
    {
      field: 'hasMentainanceWorkflow',
      title: this.translatorService.Tranlate(
        'CARFIX_ORG_MAINTENANCE_WORKFLOW',
        'Flux Mentenanta'
      ),
      render: (row: OrganizationImportItemDto) => (row.hasMentainanceWorkflow ? '1' : '0')
    },
    {
      field: 'skipSystemInvoice',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_SKIP_SYSTEM_INVOICE',
        'Nu emite factura din sistemul Connect'
      ),
      render: (row: OrganizationImportItemDto) => (row.skipSystemInvoice ? '1' : '0')
    },
    {
      field: 'isOffline',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_IS_OFFLINE',
        'Service offline'
      ),
      render: (row: OrganizationImportItemDto) => (row.isOffline ? '1' : '0')
    },
    {
      field: 'isInvoicedByGTM',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_IS_INVOICED_BY_GTM',
        'Service emite factura catre GTM, iar GTM catre client'
      ),
      render: (row: OrganizationImportItemDto) => (row.isInvoicedByGTM ? '1' : '0')
    },
    {
      field: 'isGtmManaged',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_IS_GTM_MANAGED',
        'Intervine GTM pe flux'
      ),
      render: (row: OrganizationImportItemDto) => (row.isGtmManaged ? '1' : '0')
    },
    {
      field: 'skipGeneratingDocuments',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_SKIP_GENERATING_DOCUMENTS',
        'Nu genera WO/HO'
      ),
      render: (row: OrganizationImportItemDto) =>
        row.skipGeneratingDocuments ? '1' : '0'
    },
    {
      field: 'showAppointmentService',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_SHOW_APPOINTMENT_SERVICE',
        'Vizualizeaza service programare'
      ),
      render: (row: OrganizationImportItemDto) => (row.showAppointmentService ? '1' : '0')
    },
    {
      field: 'includeToMvo',
      title: this.translatorService.Tranlate('INCLUDE_TO_MVO_LABEL', 'Include in MVO'),
      render: (row: OrganizationImportItemDto) => (row.includeToMvo ? '1' : '0')
    },
    {
      field: 'showCaseStatus',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_SHOW_CASE_STATUS',
        'Include Coloana Status'
      ),
      render: (row: OrganizationImportItemDto) => (row.showCaseStatus ? '1' : '0')
    },
    {
      field: 'hideOrganizationOwnerWorkflows',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HIDE_OWNER_WORKFLOWS',
        'Ascunde fluxuri pe care nu este partener'
      ),
      render: (row: OrganizationImportItemDto) =>
        row.hideOrganizationOwnerWorkflows ? '1' : '0'
    },
    {
      field: 'hidePartnerTab',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HIDE_PARTNER_TAB',
        'Ascunde tab dosare parteneri + intern'
      ),
      render: (row: OrganizationImportItemDto) => (row.hidePartnerTab ? '1' : '0')
    },
    {
      field: 'showMakeModelSearch',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_SHOW_MAKE_MODEL_SEARCH',
        'Cautare dupa marca + model in dosare'
      ),
      render: (row: OrganizationImportItemDto) => (row.showMakeModelSearch ? '1' : '0')
    },
    {
      field: 'hideLkqColumns',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HIDE_LKQ_COLUMNS',
        'Ascunde coloane LKQ in dosare'
      ),
      render: (row: OrganizationImportItemDto) => (row.hideLkqColumns ? '1' : '0')
    },
    {
      field: 'hideOrderCurrencyColumn',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HIDE_ORDER_CURRENCY',
        'Ascunde coloana moneda in comenzi'
      ),
      render: (row: OrganizationImportItemDto) => (row.hideOrderCurrencyColumn ? '1' : '0')
    },
    {
      field: 'viewOwnerEntries',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_VIEW_OWNER_ENTRIES',
        'Vizualizare Dosare parteneri'
      ),
      render: (row: OrganizationImportItemDto) => (row.viewOwnerEntries ? '1' : '0')
    },
    {
      field: 'hasOverduePartsNotification',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_OVERDUE_PARTS_NOTIFICATION',
        'Notificare piese intarziate'
      ),
      render: (row: OrganizationImportItemDto) =>
        row.hasOverduePartsNotification ? '1' : '0'
    },
    {
      field: 'isVehicleVinRefreshEnabled',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_VIN_REFRESH',
        'Reimprospatare date VIN din repo'
      ),
      render: (row: OrganizationImportItemDto) => (row.isVehicleVinRefreshEnabled ? '1' : '0')
    },
    {
      field: 'hoIdIssuesInvoice',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HO_ISSUES_INVOICE',
        'Factura emisa de organizatia sediu central'
      ),
      render: (row: OrganizationImportItemDto) => (row.hoIdIssuesInvoice ? '1' : '0')
    },
    {
      field: 'useGTSupplierMapping',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_USE_GT_SUPPLIER_MAPPING',
        'Foloseste mapare GT - furnizori'
      ),
      render: (row: OrganizationImportItemDto) => (row.useGTSupplierMapping ? '1' : '0')
    },
    {
      field: 'alternativePartVariantsEnabled',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_ALTERNATIVE_PART_VARIANTS_ENABLED',
        'Variante multiple pentru piese alternative'
      ),
      render: (row: OrganizationImportItemDto) =>
        row.alternativePartVariantsEnabled ? '1' : '0'
    },
    {
      field: 'hasGenericAppointmentEmail',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_HAS_GENERIC_APPOINTMENT_EMAIL',
        'Email generic programari'
      ),
      render: (row: OrganizationImportItemDto) => (row.hasGenericAppointmentEmail ? '1' : '0')
    },
    {
      field: 'isMultiBrand',
      title: this.translatorService.Tranlate('ORGANIZATIONS_IMPORT_IS_MULTI_BRAND', 'Multimarca'),
      render: (row: OrganizationImportItemDto) => (row.isMultiBrand ? '1' : '0')
    },
    {
      field: 'gtMappingQuality',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_GT_MAPPING_QUALITY',
        'Calitate mapare furnizori'
      )
    },
    {
      field: 'offlineSupportEmail',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_OFFLINE_SUPPORT_EMAIL',
        'Email suport offline'
      )
    },
    {
      field: 'latitude',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_LATITUDE_LABEL', 'Latitudine')
    },
    {
      field: 'longitude',
      title: this.translatorService.Tranlate('EDIT_ORGANIZATION_LONGITUDE_LABEL', 'Longitudine')
    },
    {
      field: 'jobTypes',
      title: this.translatorService.Tranlate('ORGANIZATIONS_IMPORT_JOB_TYPES', 'Job Type')
    },
    {
      field: 'genericAppointmentEmail',
      title: this.translatorService.Tranlate(
        'ORGANIZATIONS_IMPORT_GENERIC_APPOINTMENT_EMAIL',
        'Adresa email generic programari'
      )
    },
    {
      field: 'importMessage',
      title: this.translatorService.Tranlate(
        'MAVO_VEHICLES_VEHICLE_IMPORT_MESSAGE',
        'Rezultat import'
      )
    }
  ];

  renderImportedResultBox = () => {
    return this.state.importedOrganizationList.length > 0 ? (
      <MaterialTable
        columns={this.getImportedColumns()}
        data={this.state.importedOrganizationList}
        title=""
        icons={MaterialTableIcons}
        localization={getLocalization(this.translatorService)}
        options={{
          actionsColumnIndex: -1,
          addRowPosition: 'first',
          maxBodyHeight: 400,
          paging: false,
          sorting: false,
          search: false
        }}
        components={{
          Container: (props) => <Paper {...props} elevation={0} />
        }}
      />
    ) : (
      ''
    );
  };

  renderImportContent = () => {
    return (
      <ValidatorForm onSubmit={this.handleImport} instantValidate={true}>
        {this.renderAttachmentBox()}
        <div className="d-flex flex-row text-center flex-wrap justify-content-center">
          <ScaleLoader color={'var(--primary)'} loading={this.state.isLoadingImportData} />
        </div>
        {this.renderImportedResultBox()}
        <div className="text-right">
          <Button
            className="m-2"
            variant="outlined"
            color="default"
            onClick={this.closeImportDialog}
          >
            {this.translatorService.Tranlate('MAVO_VEHICLE_IMPORT_CLOSE_BTN', 'Renunta')}
          </Button>
          <Button
            className="m-2"
            variant="contained"
            color="secondary"
            type="submit"
            disabled={this.state.isLoadingImportData}
          >
            {this.translatorService.Tranlate('MAVO_VEHICLE_IMPORT_BTN', 'Importa')}
          </Button>
        </div>
      </ValidatorForm>
    );
  };

  render() {
    this.translatorService = (this.context as AppContext).translatorService;
    this.organizationService = (this.context as AppContext).organizationService;
    this.caseService = (this.context as AppContext).caseService;

    return (
      <Fragment>
        <Tooltip
          title={this.translatorService.Tranlate('Import', 'Importa')}
        >
          <IconButton
            aria-label="import"
            color="primary"
            className="m-2"
            onClick={this.openImportDialog}
          >
            <CloudUploadOutlined />
          </IconButton>
        </Tooltip>

        {this.state.isImportDialogOpen ? (
          <Dialog
            onClose={this.closeImportDialog}
            aria-labelledby="customized-dialog-title"
            open={this.state.isImportDialogOpen}
            fullScreen
            fullWidth
          >
            <DialogTitle
              id="customized-dialog-title"
              style={{
                color: '#ffffff',
                backgroundColor: 'var(--primary)',
                textAlign: 'center'
              }}
            >
              <span style={{ fontSize: '20px', lineHeight: '24px' }}>
                {this.translatorService.Tranlate(
                  'ORGANIZATIONS_IMPORT_MODAL_TITLE',
                  'Importa organizatii'
                )}
              </span>
              <IconButton
                aria-label="close"
                className="position-absolute"
                style={{ right: 12, top: 12, color: '#ffffff' }}
                onClick={this.closeImportDialog}
                size="small"
              >
                <CloseIcon />
              </IconButton>
            </DialogTitle>
            <DialogContent dividers>
              <Box m={1} p={1}>
                {this.renderImportContent()}
              </Box>
            </DialogContent>
          </Dialog>
        ) : (
          ''
        )}
      </Fragment>
    );
  }
}

export default withSnackbar(OrganizationImport);
